import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  Text,
  Tabs,
  SegmentedControl,
  Modal,
  ActionIcon,
  Flex,
  Stack,
  Divider,
  TextInput,
  Group,
} from "@mantine/core";
import {
  CTextInput,
  CMultiSelect,
  CSelect,
  CTextarea,
  CRadioGroup,
  CMultiSelectCreatable,
} from "@supportinitiative/react-hook-form-mantine";
import { object, string, number, date, array, setLocale } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getConfirmationStatus, getSystemStatus } from "../../../UseCaseUtils";
import { UseCaseCVMContext } from "../../../UseCaseCVMContext";
import { UAction, UNotificationActionType, UseCaseType } from "@supportinitiative/common";
import { getId } from "src/utilities";
import { IconPlus, IconTrash } from "@tabler/icons-react";

setLocale({
  mixed: {
    required: "Required",
    notType: "Invalid Value",
  },
});

const UNotificationActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required().oneOf(["NOTIFICATION"]),
  description: string().nullable(),
  definition: object({
    type: string().required().oneOf(["EMAIL", "SMS"]),
    dataPopulation: object({
      columns: array().of(
        object({
          name: string().required(),
          genFunction: string().required(),
          id: string().required(),
        }),
      ),
      rows: array().of(
        object({
          name: string().required(),
          genFunction: string().required(),
          id: string().required(),
        }),
      ),
    }),
    to: array().of(string().required()).min(1),
    subject: string().required(),
    body: string().required(),
  }),
});

function Notification({
  recon,
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: any;
  recon?: UseCaseType["definition"]["recons"][number];
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<UNotificationActionType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
        type: action.definition.type || "EMAIL",
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(UNotificationActionSchema),
  });

  const dataPopulationColumns = useFieldArray({
    control,
    name: "definition.dataPopulation.columns",
  });

  const dataPopulationRows = useFieldArray({
    control,
    name: "definition.dataPopulation.rows",
  });

  const to = useWatch({
    control,
    name: "definition.to",
  });

  const toLOV = to
    ? to.map((item) => {
        return { label: item, value: item };
      })
    : [];

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Notification</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name={`definition.type`}
              control={control}
              label={"Type"}
              data={[
                {
                  value: "EMAIL",
                  label: "EMAIL",
                },
                {
                  value: "SMS",
                  label: "SMS",
                },
              ]}
              showErrorText={false}
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider my="xs" label="Data Population" labelPosition="center" />

        <Flex>
          <Group mb="xs" style={{ alignItems: "center" }}>
            <Text fw={500} style={{ fontSize: "14px" }}>
              Columns
            </Text>
            <ActionIcon
              size="sm"
              variant="white"
              color="gray"
              onClick={() => {
                dataPopulationColumns.append({
                  name: "",
                  genFunction: "",
                  id: getId(),
                });
              }}
              disabled={useCaseFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>
        </Flex>
        <Stack gap="0" mb="lg">
          {dataPopulationColumns.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={3}>
                  <CTextInput
                    name={`definition.dataPopulation.columns.${index}.name`}
                    control={control}
                    label={"Name"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <CTextarea
                    name={`definition.dataPopulation.columns.${index}.genFunction`}
                    control={control}
                    label={"Generate Function"}
                    minRows={4}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <CTextInput
                    name={`definition.dataPopulation.columns.${index}.id`}
                    control={control}
                    label={"Id"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  />
                </Grid.Col>
                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="white"
                    color="gray"
                    onClick={() => {
                      dataPopulationColumns.remove(index);
                    }}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>

        <Flex>
          <Group mb="xs" style={{ alignItems: "center" }}>
            <Text fw={500} style={{ fontSize: "14px" }}>
              Rows
            </Text>
            <ActionIcon
              size="sm"
              variant="white"
              color="gray"
              onClick={() => {
                dataPopulationRows.append({
                  name: "",
                  genFunction: "",
                  id: getId(),
                });
              }}
              disabled={useCaseFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>
        </Flex>
        <Stack gap="0">
          {dataPopulationRows.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={3}>
                  <CTextInput
                    name={`definition.dataPopulation.rows.${index}.name`}
                    control={control}
                    label={"Name"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <CTextarea
                    name={`definition.dataPopulation.rows.${index}.genFunction`}
                    control={control}
                    label={"Generate Function"}
                    minRows={4}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <CTextInput
                    name={`definition.dataPopulation.rows.${index}.id`}
                    control={control}
                    label={"Id"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  />
                </Grid.Col>
                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="white"
                    color="gray"
                    onClick={() => {
                      dataPopulationRows.remove(index);
                    }}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>

        <Divider my="xs" label="Condition" labelPosition="center" />
        <Stack gap="0">
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextarea
                name={`definition.condition`}
                control={control}
                label="Condition"
                autosize
                minRows={5}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider my="xs" label="Details" labelPosition="center" />

        <Stack gap="0">
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CMultiSelectCreatable
                name={"definition.to"}
                placeholder="To"
                control={control}
                data={toLOV}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextInput
                name={`definition.subject`}
                control={control}
                label="Subject"
                required
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextarea
                name={`definition.body`}
                control={control}
                label="Body"
                required
                autosize
                minRows={10}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                useCaseFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await UNotificationActionSchema.validate(values, {
                          abortEarly: false,
                        });

                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default Notification;
